import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FaRegStar, FaStar } from 'react-icons/fa';
import Button from 'components/ui/button';
import { ModalFrame } from 'components/ui/modal-frame';
import { registerInteractionCampaign, verifyCampaign } from 'services/actionlog.service';
import { useAuth } from 'context/AuthContext';
import TextEditor from 'components/text-editor';
import { readOncePerSession, SESSION_KEYS, setSessionValue } from 'utils/session-reads';

interface TeamsEvaluationModalProps {
  /** O usuário fechou a modal: ✕, Esc ou fim do "obrigado" após enviar. */
  onClose: () => void;
  /**
   * A modal decidiu NÃO aparecer: a pesquisa já foi respondida (inclui "Não abrir mais") ou
   * a verificação falhou. Quem monta a modal precisa saber — sem este aviso, a fila de modais
   * do Dashboard (pages/Dashboard/modal-queue.ts) espera para sempre por uma modal que nunca
   * vai aparecer. Opcional e backward-safe.
   */
  onSkip?: () => void;
}

const ACTION_ID = 'evaluate_teams_recognition';
const THANKS_CLOSE_DELAY_MS = 3000;

const RATING_LABELS = ['Muito insatisfeito', 'Insatisfeito', 'Neutro', 'Satisfeito', 'Excelente'];
const STARS = [1, 2, 3, 4, 5];
const COMMENT_LABEL = 'Tem algum comentário ou sugestão? (opcional)';

// O mesmo checkbox do modal de regulamento. D3: 17px não tem degrau no Tailwind.
const CHECKBOX = 'h-[17px] w-[17px] shrink-0 cursor-pointer accent-[var(--brand-blue)]';

function starName(star: number) {
  return `${star} ${star === 1 ? 'estrela' : 'estrelas'} - ${RATING_LABELS[star - 1]}`;
}

interface RatingFieldProps {
  rating: number;
  onRate: (rating: number) => void;
}

/**
 * Marcada e vazia diferem pela FORMA (cheia × contorno), e a nota aparece em texto: não depende
 * só da cor (WCAG 1.4.1), como no `StarRating` do catálogo. Os rádios são nativos: um único
 * marcado e setas do teclado sem código próprio. O passar do mouse só antecipa o desenho.
 */
function RatingField({ rating, onRate }: RatingFieldProps) {
  const groupName = useId();
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? rating;

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="mb-3 w-full text-center font-bold">Queremos ouvir você: que nota dá para essa experiência?</legend>

      <div className="flex flex-col items-center gap-2">
        <div className="flex gap-2" onMouseLeave={() => setHover(null)}>
          {STARS.map(star => {
            const Icon = shown >= star ? FaStar : FaRegStar;

            return (
              <label key={star} className="cursor-pointer" onMouseEnter={() => setHover(star)}>
                <input
                  type="radio"
                  name={groupName}
                  value={star}
                  checked={rating === star}
                  onChange={() => onRate(star)}
                  aria-label={starName(star)}
                  className="peer sr-only"
                />
                <span className="block rounded-jj-xs text-brand-blue peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-blue-dark">
                  <Icon size={32} aria-hidden="true" />
                </span>
              </label>
            );
          })}
        </div>

        {/* O leitor de tela já ouve o rádio marcado. */}
        <p className="m-0 text-jj-body-sm font-bold" aria-hidden="true">
          {shown ? `${shown} de 5 · ${RATING_LABELS[shown - 1]}` : 'Sem nota'}
        </p>
      </div>
    </fieldset>
  );
}

const TeamsEvaluationModal: React.FC<TeamsEvaluationModalProps> = ({ onClose, onSkip }) => {
  const { user } = useAuth();
  const userId = user?._id;

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Valores vivos para a verificação, que só reroda quando `user._id` muda: o Dashboard passa
  // `onSkip` inline (função nova a cada render), e a falha precisa saber se a modal já está
  // na tela para não derrubá-la.
  const onSkipRef = useRef(onSkip);
  onSkipRef.current = onSkip;
  const showModalRef = useRef(showModal);
  showModalRef.current = showModal;

  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const thanksRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setShowModal(false);
    // Fechada (✕, Esc ou depois de responder), não volta até F5/login (DCE-38).
    if (userId) setSessionValue(SESSION_KEYS.teamsSurvey(userId), false);

    onClose();
  }, [onClose, userId]);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  // O botão de envio some junto com o formulário: o foco volta ao diálogo, e não ao `body`.
  useEffect(() => {
    if (feedbackSent) thanksRef.current?.closest<HTMLElement>('[role="dialog"]')?.focus();
  }, [feedbackSent]);

  // A pesquisa é decidida 1 vez por sessão por player (DCE-37): a sessão guarda se ela deve
  // aparecer. Respondida ou fechada, a remontagem chama `onSkip` sem requisitar (DCE-38/39);
  // uma nova referência de `user` (refreshUser) não reverifica (DCE-28).
  useEffect(() => {
    if (!userId) return;

    let active = true;

    readOncePerSession(SESSION_KEYS.teamsSurvey(userId), async () => {
      const result = await verifyCampaign(ACTION_ID);
      const alreadyAnswered = Array.isArray(result) && result.length >= 1;
      return !alreadyAnswered;
    })
      .then(shouldShow => {
        if (!active) return;
        setShowModal(shouldShow);

        if (!shouldShow) {
          onSkipRef.current?.();
        }
      })
      .catch(() => {
        // Sem resposta não dá para saber se a pesquisa foi respondida. Fechada, a modal segue
        // fechada (como sempre foi) e passa a vez na fila; já na tela, fica onde está.
        if (active && !showModalRef.current) {
          onSkipRef.current?.();
        }
      });

    return () => {
      active = false;
    };
  }, [userId]);

  const canSubmit = rating > 0 || dontShowAgain;

  const handleSubmit = () => {
    if (!canSubmit || feedbackSent) return;

    const payload = rating > 0 ? { rating, comment } : {};
    registerInteractionCampaign('me', ACTION_ID, payload, null, null, null, false);

    setFeedbackSent(true);
    closeTimerRef.current = setTimeout(handleClose, THANKS_CLOSE_DELAY_MS);
  };

  return (
    <ModalFrame
      isOpen={showModal}
      title="Olá, colega!"
      subtitle="Que legal! Vimos que você enviou um reconhecimento direto pelo Teams."
      size="md"
      onClose={handleClose}
      closeLabel="Fechar modal de avaliação"
      footer={
        !feedbackSent && (
          <Button variant="principal" className="w-full sm:ml-auto sm:w-auto" disabled={!canSubmit} onClick={handleSubmit}>
            {rating > 0 || !dontShowAgain ? 'Enviar avaliação' : 'Desejo não participar!'}
          </Button>
        )
      }
    >
      <div className="px-5 py-6 text-content lg:px-8">
        {/* Sempre montada: a região só anuncia o que entra nela depois de existir. */}
        <div ref={thanksRef} role="status" className="text-center">
          {feedbackSent && (
            <>
              <p className="m-0 font-display text-jj-h3 font-extrabold text-brand-blue-dark">Obrigado pela sua avaliação!</p>
              <p className="m-0 mt-2">Seu feedback é muito importante para nós.</p>
            </>
          )}
        </div>

        {!feedbackSent && (
          <div className="flex flex-col gap-6">
            <RatingField rating={rating} onRate={setRating} />

            <div>
              {/* O texto visível é o nome do editor (WCAG 2.5.3): o editor não aceita `<label>`. */}
              <p className="m-0 mb-2 text-jj-body-sm" aria-hidden="true">
                {COMMENT_LABEL}
              </p>
              <div className="h-32 text-xs">
                <TextEditor appearance="light" disableEmoji onChange={html => setComment(html)} ariaLabel={COMMENT_LABEL} />
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2.5 text-jj-body-sm">
              <input
                type="checkbox"
                className={CHECKBOX}
                checked={dontShowAgain}
                onChange={e => setDontShowAgain(e.target.checked)}
              />
              Não abrir mais essa mensagem.
            </label>
          </div>
        )}
      </div>
    </ModalFrame>
  );
};

export default TeamsEvaluationModal;
